import { Color, Typography } from '@persona-id/design-tokens';
import {
  ArrowCollapseIcon,
  ArrowExpandIcon,
  BranchIcon,
  ClockIcon,
  GridIcon,
  LayersIcon,
  ParallelIcon,
  PersonaIcon,
  RotateCwIcon,
  ZapIcon,
} from '@persona-id/design-tokens/icons';
import { default as qixColor } from 'color';
import { forwardRef, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { WorkflowRunStepStatus, WorkflowRunStepType } from 'dashboard/models/workflow-step';
import { FakeWorkflowStepType, WorkflowStepType } from 'dashboard/models/workflow-version';
import { Z_INDICES } from 'dashboard/styles/variables';
import { Elevation } from 'design-system/variables/effect';
import { TransitionDuration } from 'design-system/variables/transitions';
import { ApiModel } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

export const WORKFLOW_STEP_CARD_WIDTH = 256;
export const WORKFLOW_STEP_CARD_HEIGHT = 68;

const CARD_BORDER_WIDTH = 2;

export const workflowStepIcon = (
  type: WorkflowStepType | FakeWorkflowStepType | WorkflowRunStepType,
  size = 24,
) => {
  switch (type) {
    case WorkflowStepType.Action:
    case ApiModel.WorkflowRunStepAction:
      return <ZapIcon size={size} />;
    case WorkflowStepType.Wait:
    case ApiModel.WorkflowRunStepWait:
      return <ClockIcon size={size} />;
    case WorkflowStepType.Conditional:
    case ApiModel.WorkflowRunStepConditional:
      return <BranchIcon size={size} />;
    case WorkflowStepType.Module:
    case ApiModel.WorkflowRunStepModule:
      return <GridIcon size={size} />;
    case WorkflowStepType.Loop:
    case ApiModel.WorkflowRunStepLoop:
      return <RotateCwIcon size={size} />;
    case WorkflowStepType.Parallel:
    case ApiModel.WorkflowRunStepParallel:
      return <ParallelIcon size={size} />;
    case WorkflowStepType.Placeholder:
      return <div></div>;
    case WorkflowStepType.Input:
      return <PersonaIcon size={size} />;
    case WorkflowStepType.Output:
      return <PersonaIcon size={size} />;
    case FakeWorkflowStepType.MapOutput:
      return <RotateCwIcon size={size} />;
    default:
      return assertNever(type);
  }
};

const CardBase = styled.div<{
  collapsed: boolean;
}>`
  position: relative;
  height: ${({ collapsed }) =>
    collapsed ? WORKFLOW_STEP_CARD_HEIGHT + 12 : WORKFLOW_STEP_CARD_HEIGHT}px;
`;

const Card = styled.div<{
  active: boolean;
  collapsed: boolean;
  deactivated: boolean;
  focused: boolean;
  isPlaceholder: boolean;
  hasError: boolean;
  workflowRunStepStatus?: WorkflowRunStepStatus;
  workflowStepHighlightColor?: Color;
}>`
  position: relative;
  display: flex;
  width: ${WORKFLOW_STEP_CARD_WIDTH}px;
  height: ${WORKFLOW_STEP_CARD_HEIGHT}px;
  border-width: ${CARD_BORDER_WIDTH}px;
  border-style: ${({ isPlaceholder }) => (isPlaceholder ? 'dashed' : 'solid')};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  box-shadow: ${({ collapsed }) => (collapsed ? 'none' : Elevation.Mid)};
  padding: 14px;
  background-color: ${({ active, focused }) =>
    !active && focused ? Color.CorePurple100 : Color.PrimaryWhite};
  cursor: pointer;
  z-index: ${Z_INDICES.withShadow};
  user-select: none;
  border-color: ${({ active, hasError, workflowRunStepStatus }) => {
    if (hasError) {
      return active ? Color.CoreRed900 : Color.CoreRed800;
    } else if (workflowRunStepStatus) {
      switch (workflowRunStepStatus) {
        case WorkflowRunStepStatus.Completed:
          return active ? Color.CorePurple800 : Color.CorePurple600;
        case WorkflowRunStepStatus.Errored:
        case WorkflowRunStepStatus.Failed:
          return active ? Color.CoreRed900 : Color.CoreRed800;
        case WorkflowRunStepStatus.InProgress:
          return active ? Color.CoreYellow600 : Color.CoreYellow400;
        default:
          return active ? Color.CorePurple800 : Color.CorePurple200;
      }
    } else {
      return active ? Color.CorePurple800 : Color.CorePurple200;
    }
  }};
  opacity: ${({ workflowRunStepStatus }) => {
    return workflowRunStepStatus != null &&
      [WorkflowRunStepStatus.Initialized, WorkflowRunStepStatus.Canceled].includes(
        workflowRunStepStatus,
      )
      ? 0.4
      : 1;
  }};

  ${({ workflowStepHighlightColor, theme }) => {
    const highlightColor = qixColor(workflowStepHighlightColor).rgb();
    const highlightColorRgb = `${highlightColor.red()}, ${highlightColor.green()}, ${highlightColor.blue()}`;
    if (workflowStepHighlightColor) {
      return css`
        background-color: ${`rgba(${highlightColorRgb}, 0.2)`};
        outline: ${`rgba(${highlightColorRgb}, 0.8)`} solid ${`5px`};
        border-radius: ${theme.card.borderRadius};
      `;
    }
  }}

  ${({ deactivated }) => {
    return deactivated
      ? `
      filter: grayscale(100%);

      ${Name} {
        color: ${Color.TextSecondary}
      }

      ${Icon} {
        color: ${Color.BorderIconDisabled}
      }
    `
      : ``;
  }}
`;

const Icon = styled.div<{ active: boolean; collapsible: boolean }>`
  width: 36px;
  height: 36px;
  padding: 6px;
  border-radius: ${({ theme }) => theme.element.borderRadius};
  background-color: ${Color.CorePurple100};

  & > .workflow-step-card__icon-default svg {
    color: ${({ active }) => (active ? Color.CorePurple800 : Color.CorePurple600)};
  }

  ${({ collapsible }) => {
    if (collapsible) {
      return `
        transition: background-color ${TransitionDuration.Fast};

        & > .workflow-step-card__icon-hovered {
          display: none;
        }

        &:hover {
          background-color: ${Color.CorePurple800};

          & > .workflow-step-card__icon-default {
            display: none;
          }
          & > .workflow-step-card__icon-hovered {
            display: block;
          }
        }
      `;
    }
  }}
`;

const TypeAndIcon = styled.div`
  display: flex;
  align-items: center;
  margin-top: 2px;
  color: ${Color.TextSecondary};

  & > svg {
    margin-left: 2px;
  }
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 14px;
  overflow: hidden;
  justify-content: center;
`;

const Name = styled.div`
  ${Typography.TextMediumBold}
  color: ${Color.TextPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 20px;
`;

const Type = styled.div`
  ${Typography.TextSmall}
`;

const Separator = styled.span`
  ${Typography.TextSmall}
  margin: 0 2px;
  text-align: center;
  :after {
    content: '•';
  }
`;

const Duration = styled.div`
  ${Typography.TextSmall}
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CIRCLE_DIAMETER = 10;

const Circle = styled.div<{
  active: boolean;
  workflowRunStepStatus?: WorkflowRunStepStatus;
}>`
  border-radius: 50%;
  width: ${CIRCLE_DIAMETER}px;
  height: ${CIRCLE_DIAMETER}px;
  position: absolute;
  background-color: ${({ active, workflowRunStepStatus }) => {
    if (!active) {
      return Color.CorePurple200;
    } else if (workflowRunStepStatus) {
      switch (workflowRunStepStatus) {
        case WorkflowRunStepStatus.Completed:
          return Color.CorePurple600;
        case WorkflowRunStepStatus.Errored:
        case WorkflowRunStepStatus.Failed:
          return Color.CoreRed900;
        case WorkflowRunStepStatus.InProgress:
          return Color.CoreYellow600;
        default:
          return Color.CorePurple800;
      }
    }
    return Color.CorePurple800;
  }};
  left: ${(WORKFLOW_STEP_CARD_WIDTH - CIRCLE_DIAMETER) / 2 - CARD_BORDER_WIDTH}px;
`;

const TopCircle = styled(Circle)`
  top: ${-(CIRCLE_DIAMETER + CARD_BORDER_WIDTH) / 2}px;
`;

const BottomCircle = styled(Circle)`
  bottom: ${-(CIRCLE_DIAMETER + CARD_BORDER_WIDTH) / 2}px;
`;

const StackedCard = styled.div<{
  index: number;
}>`
  top: ${({ index }) => WORKFLOW_STEP_CARD_HEIGHT / 2 + (index + 1) * 6}px;
  position: absolute;
  width: ${WORKFLOW_STEP_CARD_WIDTH}px;
  height: 34px;
  box-shadow: ${({ index }) => (index === 1 ? Elevation.Mid : 'none')};
  border-width: ${CARD_BORDER_WIDTH}px;
  border-style: solid;
  border-radius: ${({ theme }) => theme.card.borderRadius};
  border-color: ${Color.CorePurple200};
`;

interface WorkflowStepCardProps {
  id?: string;
  name: string;
  type: WorkflowStepType | FakeWorkflowStepType;
  isInitial?: boolean;
  isLast?: boolean;
  active?: boolean;
  deactivated?: boolean;
  focused: boolean;
  hasError: boolean;
  token: string;
  workflowRunStepStatus?: WorkflowRunStepStatus;
  workflowStepHighlightColor?: Color;
  collapsed?: boolean;
  onClickCollapseIcon?: () => void;
  duration?: string | null;
}

/**
 * This component is responsible for rendering an individual workflow step or
 * trigger.
 */
export const WorkflowStepCard = forwardRef<HTMLDivElement, WorkflowStepCardProps>(
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  (
    {
      id,
      name,
      type,
      isInitial,
      isLast,
      active = false,
      deactivated = false,
      focused,
      hasError,
      token,
      workflowRunStepStatus,
      workflowStepHighlightColor,
      collapsed = false,
      duration,
      onClickCollapseIcon,
    }: WorkflowStepCardProps,
    forwardedRef: (instance: HTMLDivElement) => void,
  ) => {
    const [cardType, cardIcon] = useMemo(() => {
      /* eslint-disable react/jsx-key */
      switch (type) {
        case WorkflowStepType.Input:
          return ['Trigger', <PersonaIcon size='24' />];
        case WorkflowStepType.Action:
          return ['Action', workflowStepIcon(type)];
        case WorkflowStepType.Wait:
          return ['Wait', workflowStepIcon(type)];
        case WorkflowStepType.Conditional:
          return ['Conditional', workflowStepIcon(type)];
        case WorkflowStepType.Module:
          return ['Module', workflowStepIcon(type)];
        case WorkflowStepType.Loop:
          return ['Loop', workflowStepIcon(type)];
        case WorkflowStepType.Parallel:
          return ['Parallel', workflowStepIcon(type)];
        case WorkflowStepType.Placeholder:
          return ['', workflowStepIcon(type)];
        case WorkflowStepType.Output:
          return ['Output', workflowStepIcon(type)];
        case FakeWorkflowStepType.MapOutput:
          return ['Map Output', workflowStepIcon(type)];
        default:
          return assertNever(type);
      }
      /* eslint-enable react/jsx-key */
    }, [type]);
    const isPlaceholder = type === WorkflowStepType.Placeholder;
    return (
      <CardBase collapsed={collapsed}>
        <Card
          active={active}
          collapsed={collapsed}
          role='treeitem'
          aria-label={`workflow step card for ${token}`}
          deactivated={deactivated}
          focused={focused}
          hasError={hasError}
          id={id}
          isPlaceholder={isPlaceholder}
          ref={forwardedRef}
          workflowRunStepStatus={workflowRunStepStatus}
          workflowStepHighlightColor={workflowStepHighlightColor}
        >
          {!isInitial && (
            <TopCircle
              active={active}
              role='mark'
              aria-label='top circle'
              workflowRunStepStatus={workflowRunStepStatus}
            />
          )}
          {!isLast && !collapsed && (
            <BottomCircle
              active={active}
              role='mark'
              aria-label='bottom circle'
              workflowRunStepStatus={workflowRunStepStatus}
            />
          )}
          <Icon active={active} collapsible={!!onClickCollapseIcon}>
            <div className='workflow-step-card__icon-default'>{cardIcon}</div>
            {onClickCollapseIcon && (
              <div
                id={`workflow-step-card__icon-${id}`}
                className='workflow-step-card__icon-hovered'
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClickCollapseIcon();
                }}
              >
                {collapsed ? (
                  <ArrowExpandIcon color={Color.PrimaryWhite} size='24' />
                ) : (
                  <ArrowCollapseIcon color={Color.PrimaryWhite} size='24' />
                )}
              </div>
            )}
          </Icon>
          {!isPlaceholder && (
            <Text title={name}>
              <Name>{name}</Name>
              <TypeAndIcon>
                <Type>{cardType}</Type>
                {duration && (
                  <>
                    <Separator />
                    <Duration>{duration}</Duration>
                  </>
                )}
                {collapsed && <LayersIcon size={10} />}
              </TypeAndIcon>
            </Text>
          )}
        </Card>
        {collapsed && (
          <>
            <StackedCard index={0} />
            <StackedCard index={1} />
          </>
        )}
      </CardBase>
    );
  },
);

WorkflowStepCard.displayName = 'WorkflowStepCard';
